/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { Switch, Route, withRouter } from 'react-router-dom';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import moment from 'moment';
import MomentUtils from '@date-io/moment';
import { GeneralContext } from 'context/';

import Profile from 'containers/Profile/Loadable';
import AppRoute from 'components/AppRoute/Loadable';
import ConfirmationMessage from 'components/ConfirmationMessage/Loadable';
import GeneralLayout from 'containers/GeneralLayout/Loadable';
import FormsLayout from 'containers/FormsLayout/Loadable';
import Login from 'containers/Login/Loadable';
import AvisoPrivacidad from 'containers/AvisoPrivacidad/Loadable';
import AuthPage from 'containers/Login/AuthPage';
import Loading from 'components/FullPageLoader/Loadable';
import Dashboard from 'containers/Dashboard/Loadable';
import Prestamos from 'containers/Prestamos/Loadable';
import NewEditPrestamos from 'containers/Prestamos/NewEdit/Loadable';
import PayPrestamo from 'containers/Prestamos/Pay/Loadable';
import Trabajadores from 'containers/Trabajadores/Loadable';
import NewEditTrabajadores from 'containers/Trabajadores/NewEdit/Loadable';
import ImportTrabajadores from 'containers/Trabajadores/Import/Loadable';
import Clientes from 'containers/Clientes/Loadable';
import NewEditClientes from 'containers/Clientes/NewEdit/Loadable';
import Roles from 'containers/Roles/Loadable';
import NewEditRole from 'containers/Roles/NewEdit/Loadable';
import Users from 'containers/Users/Loadable';
import NewEditUsers from 'containers/Users/NewEditUsers/Loadable';
import Notifications from 'containers/Notifications/Loadable';
import CortesAnticipo from 'containers/CortesList/Loadable';
import CortesAnticipoNuevo from 'containers/CortesNew/Loadable';
import ApolloFetch from 'utils/apolloFetch';
import { GET_STATUS_LIST } from 'graphql/queries/statuses';
import GlobalStyle from 'global-styles';

const AppWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  min-height: 100%;
  flex-direction: column;
`;

const globalFont = createMuiTheme({
  typography: {
    fontFamily: ['Avenir', 'Helvetica Neue', 'Arial'].join(','),
    useNextVariants: true,
  },
});

MomentUtils.prototype.getStartOfMonth = MomentUtils.prototype.startOfMonth;

export function App({ history, location }) {
  const generalContext = React.useContext(GeneralContext);
  const {
    loading: fetching,
    showMessage,
    setShowMessage,
    textMessage,
    errorMessage,
  } = generalContext;

  function handleCloseMessage() {
    setShowMessage(false);
  }

  async function getStatusList() {
    const apolloFetchDefault = new ApolloFetch(GET_STATUS_LIST);
    const statusResponse = await apolloFetchDefault.getDefaults();
    if (!isEmpty(statusResponse) && !isEmpty(statusResponse.status)) {
      localStorage.setItem('KNomStatusList', JSON.stringify(statusResponse.status));
    }
  }

  useEffect(() => {
    getStatusList();
    // Determinando si existe sesión abierta
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user && location.pathname !== '/aviso-privacidad') history.push('/');
  }, [history]);

  return (
    <MuiPickersUtilsProvider utils={MomentUtils} locale="en" libInstance={moment}>
      <MuiThemeProvider theme={globalFont}>
        <AppWrapper>
          <Helmet titleTemplate="%s AdN+" defaultTitle="AdN+">
            <meta name="description" content="Backoffice AdN+" />
          </Helmet>
          <Switch>
            <Route exact path="/" component={Login} />
            <Route exact path="/auth/google" component={AuthPage} />
            <Route exact path="/aviso-privacidad" component={AvisoPrivacidad} />
            <AppRoute
              layout={GeneralLayout}
              exact
              path="/dashboard"
              component={Dashboard}
            />
            <AppRoute
              layout={GeneralLayout}
              exact
              path="/trabajadores"
              component={Trabajadores}
            />
            <AppRoute
              layout={FormsLayout}
              exact
              path="/trabajadores/new"
              component={NewEditTrabajadores}
            />
            <AppRoute
              layout={FormsLayout}
              exact
              path="/trabajadores/edit"
              component={NewEditTrabajadores}
            />
            <AppRoute
              layout={FormsLayout}
              exact
              path="/trabajadores/import"
              component={ImportTrabajadores}
            />
            <AppRoute
              layout={GeneralLayout}
              exact
              path="/anticipos"
              component={Prestamos}
            />
            <AppRoute
              layout={FormsLayout}
              exact
              path="/anticipos/new"
              component={NewEditPrestamos}
            />
            <AppRoute
              layout={FormsLayout}
              exact
              path="/anticipos/edit"
              component={NewEditPrestamos}
            />
            <AppRoute layout={GeneralLayout} exact path="/clientes" component={Clientes} />
            <AppRoute
              layout={FormsLayout}
              exact
              path="/clientes/new"
              component={NewEditClientes}
            />
            <AppRoute
              layout={FormsLayout}
              exact
              path="/clientes/edit"
              component={NewEditClientes}
            />
            <AppRoute
              layout={FormsLayout}
              exact
              path="/anticipos/pay"
              component={PayPrestamo}
            />
            <AppRoute
              layout={GeneralLayout}
              exact
              path="/anticipos/cortes"
              component={CortesAnticipo}
            />
            <AppRoute
              layout={FormsLayout}
              exact
              path="/anticipos/cortes/new"
              component={CortesAnticipoNuevo}
            />
            <AppRoute layout={GeneralLayout} exact path="/roles" component={Roles} />
            <AppRoute
              layout={FormsLayout}
              exact
              path="/roles/new"
              component={NewEditRole}
            />
            <AppRoute
              layout={FormsLayout}
              exact
              path="/roles/edit"
              component={NewEditRole}
            />
            <AppRoute layout={GeneralLayout} exact path="/users" component={Users} />
            <AppRoute
              layout={FormsLayout}
              exact
              path="/users/new"
              component={NewEditUsers}
            />
            <AppRoute
              layout={FormsLayout}
              exact
              path="/users/edit"
              component={NewEditUsers}
            />
            <AppRoute layout={FormsLayout} exact path="/perfil" component={Profile} />
            <AppRoute
              layout={GeneralLayout}
              exact
              path="/notifications"
              component={Notifications}
            />
          </Switch>
          <GlobalStyle />
          {fetching && <Loading />}
          {showMessage && (
            <ConfirmationMessage
              message={textMessage || errorMessage}
              isError={errorMessage !== null}
              onClose={handleCloseMessage}
            />
          )}
        </AppWrapper>
      </MuiThemeProvider>
    </MuiPickersUtilsProvider>
  );
}

App.propTypes = {
  history: PropTypes.objectOf(PropTypes.any).isRequired,
  location: PropTypes.objectOf(PropTypes.any),
};

export default withRouter(App);
